import { defineComponent as _defineComponent } from 'vue';
import { createVNode as _createVNode, resolveComponent as _resolveComponent, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withModifiers as _withModifiers, createBlock as _createBlock, createTextVNode as _createTextVNode } from "vue";
const _hoisted_1 = ["title", "disabled"];
const _hoisted_2 = ["title", "disabled"];
import { computed, ref, watch } from 'vue';
import { useI18n } from "vue-i18n";
import { tt } from "@astina/paloma-common-ui/src/services/Translator";
import Spinner from "@astina/paloma-common-ui/src/components/Spinner.vue";
import ArticleCartInput from "./ArticleCartInput.vue";
import ConfirmationDialog from "@astina/paloma-common-ui/src/components/ConfirmationDialog.vue";
import config from "../services/ConfigRepository";
import availabilityService from "../services/AvailabilityService";
export default /*@__PURE__*/ _defineComponent({
    __name: 'ArticleCartForm',
    props: {
        variant: {},
        cart: {},
        addCartItems: { type: Function },
        addWatchlistItem: { type: Function },
        tabIndex: {},
        considerUnavailabilityReasons: { type: Boolean },
        allowOrderingUnitSelection: { type: Boolean }
    },
    emits: [
        'quantity-change',
        'cart-item-draft-change'
    ],
    setup(__props, { emit: __emit }) {
        const { t } = useI18n();
        const props = __props;
        const emit = __emit;
        const itemDraft = ref(null);
        const loading = ref(false);
        const ordersUiConfig = ref({ allowedUnavailabilityReasons: [] });
        const showUnavailabilityConfirmationModal = ref(false);
        /**
         * For debugging only. Indicates which `return` was used in `submitDisabled`.
         */
        const submitDisabledReturn = ref(null);
        const submitDisabled = computed(() => {
            if (loading.value) {
                submitDisabledReturn.value = 'loading';
                return true;
            }
            if (itemDraft.value == null) {
                submitDisabledReturn.value = 'itemDraftIsNull';
                return true;
            }
            if (!hasOrderingUnits()) {
                submitDisabledReturn.value = 'orderingUnits';
                return true;
            }
            if (itemDraft.value && itemDraft.value.quantity <= 0) {
                submitDisabledReturn.value = 'quantity';
                return true;
            }
            // we're already handling the "out-of-stock" unavailability reason by
            //   tracking stock updates and setting a max quantity value, so we
            //   don't want to check for it, here! Compared to our stock updates,
            //   the variant is mostly outdated.
            if (props.variant.availability?.unavailabilityReasons.length === 1
                && props.variant.availability.unavailabilityReasons.includes('out_of_stock')) {
                submitDisabledReturn.value = 'out_of_stock';
                return false;
            }
            if (props.considerUnavailabilityReasons) {
                submitDisabledReturn.value = 'unavailabilityReasons';
                return availabilityService.isUnavailableAndNoAllowedReason(props.variant, ordersUiConfig.value);
            }
            submitDisabledReturn.value = 'unavailable';
            return !props.variant.availability.available;
        });
        if (props.considerUnavailabilityReasons) {
            loading.value = true;
            config.getConfig()
                .then(response => {
                ordersUiConfig.value = response;
            })
                .finally(() => loading.value = false);
        }
        else {
            onVariantChange();
        }
        watch(() => props.variant, onVariantChange);
        function onVariantChange() {
            refreshItemDraft();
        }
        watch(itemDraft, onItemDraftChange, { deep: true });
        function isAddWatchlistDisabled() {
            if (!itemDraft.value || !hasOrderingUnits()) {
                return true;
            }
            const orderingUnit = props.variant.orderingUnits.find(ou => ou.unitCode === itemDraft.value.unit);
            if (!orderingUnit) {
                return true;
            }
            return orderingUnit.portioning === 'required' && !itemDraft.value.portions;
        }
        function confirmUnavailability() {
            showUnavailabilityConfirmationModal.value = false;
            if (itemDraft.value) {
                const item = {
                    ...itemDraft.value,
                    allowedUnavailabilityReasons: availabilityService.getUnavailabilityReasons(ordersUiConfig.value)
                };
                props.addCartItems([item], props.cart ? props.cart.id : undefined)
                    .then(onVariantChange)
                    .finally(() => loading.value = false);
            }
        }
        function cancelUnavailability() {
            if (itemDraft.value) {
                loading.value = false;
            }
            showUnavailabilityConfirmationModal.value = false;
        }
        function hasOrderingUnits() {
            return props.variant.orderingUnits.length > 0;
        }
        function onSubmit() {
            loading.value = true;
            if (props.considerUnavailabilityReasons && availabilityService.isUnavailableAndAllowedReason(props.variant, ordersUiConfig.value)) {
                showUnavailabilityConfirmationModal.value = true;
                return;
            }
            if (itemDraft.value) {
                props.addCartItems([itemDraft.value], props.cart ? props.cart.id : undefined)
                    .finally(() => loading.value = false);
            }
        }
        function confirmBtnLabel() {
            return t('order.add_item_override');
        }
        function addToWatchlist() {
            props.addWatchlistItem
                && itemDraft.value
                && props.addWatchlistItem(itemDraft.value);
        }
        function onInputChange(newItemDraft) {
            itemDraft.value = newItemDraft;
            if (itemDraft.value) {
                emit('quantity-change', itemDraft.value.quantity);
            }
        }
        function unavailabilityTitle() {
            const firstReason = props.variant.availability.unavailabilityReasons[0];
            return tt(`order.article_${firstReason}`, 'order.article_unavailable');
        }
        function unavailabilityText() {
            const firstReason = props.variant.availability.unavailabilityReasons[0];
            return tt(`order.article_${firstReason}_text`, 'order.article_unavailable_text');
        }
        function refreshItemDraft() {
            itemDraft.value = {
                sku: props.variant.sku,
                unit: props.variant.baseOrderingUnit,
                quantity: props.variant.defaultOrderQuantity || 1,
            };
        }
        function onItemDraftChange() {
            emit('cart-item-draft-change', itemDraft.value);
        }
        return (_ctx, _cache) => {
            const _component_icon = _resolveComponent("icon");
            return (_openBlock(), _createElementBlock("form", {
                onSubmit: _withModifiers(onSubmit, ["prevent"]),
                class: "article-cart-form"
            }, [
                _createVNode(ArticleCartInput, {
                    variant: __props.variant,
                    "unit-code": itemDraft.value ? itemDraft.value.unit : undefined,
                    onChange: onInputChange,
                    "initial-quantity": itemDraft.value ? itemDraft.value.quantity : undefined,
                    cart: __props.cart,
                    "tab-index": __props.tabIndex,
                    "consider-unavailability-reasons": __props.considerUnavailabilityReasons,
                    "allow-ordering-unit-selection": __props.allowOrderingUnitSelection
                }, null, 8, ["variant", "unit-code", "initial-quantity", "cart", "tab-index", "consider-unavailability-reasons", "allow-ordering-unit-selection"]),
                _cache[1] || (_cache[1] = _createTextVNode()),
                _createVNode(Spinner, { loading: loading.value }, null, 8, ["loading"]),
                _cache[2] || (_cache[2] = _createTextVNode()),
                (!loading.value)
                    ? (_openBlock(), _createElementBlock("button", {
                        key: 0,
                        type: "submit",
                        class: "button button--link button--small",
                        title: hasOrderingUnits() ? _ctx.$t('product.add_to_order') : _ctx.$t('product.no_variants_with_ordering_units'),
                        disabled: submitDisabled.value || !itemDraft.value
                    }, [
                        _createVNode(_component_icon, { name: "cart" })
                    ], 8, _hoisted_1))
                    : _createCommentVNode("", true),
                _cache[3] || (_cache[3] = _createTextVNode()),
                (__props.addWatchlistItem)
                    ? (_openBlock(), _createElementBlock("button", {
                        key: 1,
                        onClick: _cache[0] || (_cache[0] = _withModifiers(($event) => (addToWatchlist()), ["prevent"])),
                        type: "button",
                        class: "button button--link button--small",
                        title: _ctx.$t('product.add_to_watchlist'),
                        disabled: isAddWatchlistDisabled()
                    }, [
                        _createVNode(_component_icon, { name: "watchlist" })
                    ], 8, _hoisted_2))
                    : _createCommentVNode("", true),
                _cache[4] || (_cache[4] = _createTextVNode()),
                (showUnavailabilityConfirmationModal.value)
                    ? (_openBlock(), _createBlock(ConfirmationDialog, {
                        key: 2,
                        onCancel: cancelUnavailability,
                        onConfirm: confirmUnavailability,
                        dangerous: false,
                        title: unavailabilityTitle(),
                        text: unavailabilityText(),
                        "confirm-icon": "add",
                        "confirm-label": confirmBtnLabel()
                    }, null, 8, ["title", "text", "confirm-label"]))
                    : _createCommentVNode("", true)
            ], 32));
        };
    }
});
